// Permissions management (under System Admin): one row per PERSON, each row
// opening an inline editor that adds, removes, and re-LEVELS that person's areas
// in one save, plus the bulk-add form for people who hold nothing yet.
//
// A grant is (area, access): ticking an area grants it, and the Read only / Full
// access toggle beside the tick decides how much of it. Read only is a real
// grant, not a lesser one — the pages open and the server refuses every write —
// so it is picked per area rather than being a mode over the whole console (which
// is what `viewer_only` was, until migration 0120).
//
// The table used to be one row per GRANT with a Revoke button, which meant an
// operator holding five areas occupied five rows, adding a sixth meant scrolling
// back to the form and retyping their address, and there was nowhere to see "what
// does this person actually have" in one place. The grouping lives in
// `lib/grant-rows.ts`; this file is the surface over it.
import { Fragment, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyHomeEndKey,
  Badge,
  Button,
  ConfirmDialog,
  Input,
  matchesQuery,
  SortableTh,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  usePagedRows,
  useRestoredState,
  useTableSort,
  useToast,
} from "@recruiter/shared";

import { ApiError, addPermissions, revokePermission } from "../lib/api";
import {
  ACCESS_LEVELS,
  ACCESS_META,
  AREA_LIST,
  AREA_META,
  type Access,
  type Area,
  type AreaAccess,
  newGrantLevel,
  offeredLevels,
} from "../lib/areas";
import { parseEmailList } from "../lib/email-parse";
import { formatDateTime } from "../lib/format";
import {
  type GrantedPerson,
  NO_GRANTOR,
  type SaveConfirmation,
  areaChanges,
  asGrants,
  describeAreaChanges,
  grantorLabels,
  grantsToWrite,
  groupGrantsByPerson,
  hasAreaChanges,
  levelled,
  saveConfirmation,
  saveSequence,
  summarizeAreaChanges,
  toggled,
} from "../lib/grant-rows";
import { accessLevelsSupportedQuery, adminKeys, meQuery, permissionsQuery } from "../lib/queries";
import { AreaBoundary } from "../components/area-gate";
import { AREA_ICON, CheckIcon, EyeIcon } from "../components/icons";
import { EmptyState, TableSkeleton, errorMessage } from "../components/states";
import { ShowMoreRow } from "../components/show-more-row";
import { useWriteAccess } from "../lib/use-access";
import { Link } from "@tanstack/react-router";

import { BudgetThresholds } from "../components/budget-thresholds";
import { ExperimentDials } from "../components/experiment-dials";
import { PlatformControl } from "./platform-control";
import "./permissions.css";

/** The chip grid both the bulk-add form and the per-person editor use, so one
 *  area reads the same wherever it is picked. `pending` tints a chip the save
 *  has not applied yet: dashed accent for an area about to be added or re-levelled,
 *  dashed danger for one about to be removed.
 *
 *  The level toggle sits OUTSIDE the label element rather than inside it: a
 *  button nested in a label steals the label's click and toggles the checkbox,
 *  so picking Read only would silently revoke the area you were levelling. It
 *  renders only for a ticked area, because a level on something nobody holds is
 *  a control with nothing behind it. */
function AreaChips({
  selected,
  onToggle,
  onSetAccess,
  levelsSupported,
  pendingOf,
  titleOf,
}: {
  selected: ReadonlyMap<Area, Access>;
  onToggle: (area: Area) => void;
  onSetAccess: (area: Area, access: Access) => void;
  levelsSupported: boolean;
  pendingOf?: (area: Area) => "add" | "change" | "remove" | undefined;
  titleOf?: (area: Area) => string | undefined;
}) {
  return (
    <div className="ad-areas__chips">
      {AREA_LIST.map((area) => {
        const AreaIcon = AREA_ICON[area.key];
        const access = selected.get(area.key);
        const checked = access !== undefined;
        const levels = offeredLevels(area.key, levelsSupported);
        return (
          <div
            key={area.key}
            className="ad-chip-group"
            data-checked={checked || undefined}
            data-pending={pendingOf?.(area.key)}
          >
            <label className="ad-chip" title={titleOf?.(area.key)}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(area.key)} />
              <span className="ad-chip__icon" aria-hidden="true">
                <AreaIcon />
              </span>
              <span className="ad-chip__label">{area.label}</span>
              <span className="ad-chip__check" aria-hidden="true">
                <CheckIcon size={16} />
              </span>
            </label>
            {checked && levels.length > 1 ? (
              <div className="ad-levels" role="group" aria-label={`${area.label} access level`}>
                {levels.map((level) => {
                  // An eye for Read only, a tick for Full access: nine rows of
                  // two words sharing a prefix are slow to scan, and a mark
                  // beside each makes the grid readable at a glance.
                  const LevelIcon = level === "read" ? EyeIcon : CheckIcon;
                  return (
                    <button
                      key={level}
                      type="button"
                      className="ad-level"
                      data-active={access === level || undefined}
                      aria-pressed={access === level}
                      title={ACCESS_META[level].blurb}
                      onClick={() => onSetAccess(area.key, level)}
                    >
                      <LevelIcon size={14} />
                      {ACCESS_META[level].label}
                    </button>
                  );
                })}
              </div>
            ) : checked ? (
              // ONE meaningful level: say what the grant confers instead of
              // asking. A toggle here would offer a choice no route reads —
              // "User Administration, Read only" is a row that unlocks nothing,
              // and it reads like access that was conferred. The level shown is
              // the one HELD, so a grant carried to `full` by migration 0120 on
              // an area that now offers only `read` still reads honestly.
              <span className="ad-level-fixed">{ACCESS_META[access].label}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function BulkAddForm() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [raw, setRaw] = useState("");
  const [selected, setSelected] = useState<ReadonlyMap<Area, Access>>(new Map());
  // Levels chosen during this session, so unticking an area and changing your
  // mind does not quietly reset it to the default. Nothing renders from it, so a
  // ref rather than state.
  const remembered = useRef(new Map<Area, Access>());

  const levelsSupported = useQuery(accessLevelsSupportedQuery).data ?? false;

  const parsed = useMemo(() => parseEmailList(raw), [raw]);

  const addMutation = useMutation({
    // One call per LEVEL: a grant request carries one level, and a form that
    // mixes them is at most two calls. Serial, so a failure names the level it
    // stopped on — and in `grantsToWrite`'s order, which puts `full` first. That
    // matters here too: a bulk grant of System Admin at Read only whose email
    // list includes the operator sending it is a self-demotion, and it has to be
    // the last call or the one after it 403s.
    mutationFn: async () => {
      let granted = 0;
      for (const { access, areas } of grantsToWrite({
        added: asGrants(selected),
        changed: [],
        removed: [],
      })) {
        granted += (await addPermissions(parsed.valid, areas, access)).granted;
      }
      return { granted };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.permissions });
      void queryClient.invalidateQueries({ queryKey: adminKeys.myAreas });
      setRaw("");
      setSelected(new Map());
      toast.add({
        title: `Granted ${result.granted} new ${result.granted === 1 ? "grant" : "grants"}`,
        data: { tone: "success" },
      });
    },
    onError: (error) => {
      const detail =
        error instanceof ApiError && error.status === 422
          ? "One or more emails were rejected as invalid."
          : errorMessage(error);
      toast.add({ title: "Couldn't grant access", description: detail, data: { tone: "danger" } });
    },
  });

  const canSubmit = parsed.valid.length > 0 && selected.size > 0 && !addMutation.isPending;

  return (
    <form
      className="ad-grant-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) addMutation.mutate();
      }}
    >
      <h2 className="ad-section__title">Grant access</h2>
      <p className="ad-form-hint">
        Adds areas to people in bulk. To change one person, or to move them between Read only and
        Full access, use Manage on their row below.
      </p>
      <label className="ad-field">
        <span className="ad-field__label">Emails</span>
        <textarea
          className="ad-textarea"
          rows={3}
          placeholder="one or many, comma, space, or newline separated"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={applyHomeEndKey}
          aria-label="Emails to grant"
        />
      </label>

      <div className="ad-parse-summary">
        {parsed.valid.length > 0 ? (
          <span className="ad-parse-ok">
            {parsed.valid.length} valid {parsed.valid.length === 1 ? "email" : "emails"}
          </span>
        ) : (
          <span className="ad-muted">No valid emails yet</span>
        )}
        {parsed.invalid.length > 0 ? (
          <span className="ad-parse-bad">
            Ignoring {parsed.invalid.length}: {parsed.invalid.join(", ")}
          </span>
        ) : null}
      </div>

      <fieldset className="ad-areas">
        <legend className="ad-field__label">Areas</legend>
        <p className="ad-form-hint">
          Each area is granted as Full access or Read only. Read only opens the area's pages with
          every control removed, and the server refuses the write too.
        </p>
        <AreaChips
          selected={selected}
          levelsSupported={levelsSupported}
          onToggle={(area) =>
            setSelected((prev) =>
              toggled(prev, area, remembered.current, newGrantLevel(area, levelsSupported)),
            )
          }
          onSetAccess={(area, access) => {
            remembered.current.set(area, access);
            setSelected((prev) => levelled(prev, area, access));
          }}
        />
      </fieldset>

      <Button type="submit" variant="solid" tone="accent" disabled={!canSubmit}>
        {addMutation.isPending ? "Granting…" : "Grant access"}
      </Button>
    </form>
  );
}

/** A confirmation the operator has yet to answer, plus the save it guards. The
 *  copy and the policy behind it live in `lib/grant-rows.ts`. */
type PendingSave = SaveConfirmation & { next: AreaAccess[] };

/** The inline editor: every area as a toggle with its level, pre-filled with what
 *  this person holds, saved as one diff. Adds and level changes go in one bulk
 *  call per level (the grant route upserts), removals one revoke each, which is
 *  the whole of the permissions API.
 *
 *  Remounted by its caller whenever the person's areas change (the key carries
 *  them), so a save, or another admin's change arriving on a refetch, always
 *  leaves the toggles showing the server's truth rather than a stale draft. */
function AreaEditor({ person, onClose }: { person: GrantedPerson; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const me = useQuery(meQuery);
  // Three-state on purpose. `/me` is fetched at boot but the page renders
  // without waiting for it (and it never retries on failure), so "not loaded
  // yet" must not read as "not you" — that is how a self-lockout slips through
  // unconfirmed. `null` here means unknown, and `saveConfirmation` treats
  // unknown as possibly-self.
  const myEmail = me.data?.email?.toLowerCase() ?? null;
  const isSelf = myEmail === null ? null : myEmail === person.email;

  const levelsSupported = useQuery(accessLevelsSupportedQuery).data ?? false;
  const [selected, setSelected] = useState<ReadonlyMap<Area, Access>>(
    () => new Map(person.areas.map((g) => [g.area, g.access])),
  );
  // Seeded from what they HOLD, so unticking an area and putting it back
  // proposes no change at all rather than promoting a Read only grant to Full.
  const remembered = useRef(new Map<Area, Access>(person.areas.map((g) => [g.area, g.access])));
  const [pending, setPending] = useState<PendingSave | null>(null);

  const changes = useMemo(
    () => areaChanges(person.areas, asGrants(selected)),
    [person.areas, selected],
  );
  const dirty = hasAreaChanges(changes);

  const saveMutation = useMutation({
    mutationFn: async (next: readonly AreaAccess[]) => {
      const diff = areaChanges(person.areas, next);
      // `saveSequence` owns the ORDER, and the order is a correctness property,
      // not a style: grants before revokes so a level move never passes through
      // "holds nothing", and every `system_admin` operation last so a save that
      // demotes or revokes the CALLER's own authority cannot 403 the rest of
      // itself. Serial, so a failure part-way names the area it stopped on
      // rather than leaving several in-flight writes with one unattributable
      // error.
      for (const op of saveSequence(diff)) {
        if (op.kind === "grant") await addPermissions([person.email], op.areas, op.access);
        else await revokePermission(person.email, op.area);
      }
      return diff;
    },
    onSuccess: (diff) => {
      setPending(null);
      toast.add({
        title: `Updated ${person.email}`,
        description: describeAreaChanges(diff),
        data: { tone: "success" },
      });
    },
    onError: (error) => {
      setPending(null);
      toast.add({
        title: "Couldn't update access",
        description: errorMessage(error),
        data: { tone: "danger" },
      });
    },
    // Both paths refetch: a partial failure leaves some writes applied, and the
    // table must show what actually landed. My-areas too, so the sidebar drops
    // an area the moment an operator removes it from themselves.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.permissions });
      void queryClient.invalidateQueries({ queryKey: adminKeys.myAreas });
    },
  });

  function submit(next: AreaAccess[]) {
    const diff = areaChanges(person.areas, next);
    if (!hasAreaChanges(diff)) return;
    const confirmation = saveConfirmation(person, next, diff, isSelf);
    if (confirmation) setPending({ ...confirmation, next });
    else saveMutation.mutate(next);
  }

  const busy = saveMutation.isPending;
  const summary = summarizeAreaChanges(changes);

  return (
    <div className="ad-editor">
      <div className="ad-editor__head">
        <span className="ad-editor__title">Areas for {person.email}</span>
        <span className="ad-editor__meta">
          {person.areas.length === 0
            ? "Holds nothing right now"
            : `First granted ${formatDateTime(person.firstGrantedAt)}`}
        </span>
      </div>

      <AreaChips
        selected={selected}
        levelsSupported={levelsSupported}
        onToggle={(area) =>
          setSelected((prev) =>
            toggled(prev, area, remembered.current, newGrantLevel(area, levelsSupported)),
          )
        }
        onSetAccess={(area, access) => {
          remembered.current.set(area, access);
          setSelected((prev) => levelled(prev, area, access));
        }}
        pendingOf={(area) => {
          const held = person.areas.find((g) => g.area === area);
          const wanted = selected.get(area);
          if (wanted !== undefined && !held) return "add";
          if (wanted === undefined && held) return "remove";
          if (wanted !== undefined && held && wanted !== held.access) return "change";
          return undefined;
        }}
        titleOf={(area) => {
          const grant = person.grantByArea[area];
          if (!grant) return undefined;
          const when = formatDateTime(grant.createdAt);
          const level = ACCESS_META[grant.access].label;
          return grant.grantedBy
            ? `${level}, granted by ${grant.grantedBy} on ${when}`
            : `${level}, granted on ${when}. Grantor ${NO_GRANTOR.toLowerCase()}.`;
        }}
      />

      <div className="ad-editor__actions">
        <Button
          type="button"
          variant="solid"
          tone="accent"
          size="sm"
          disabled={!dirty || busy}
          onClick={() => submit(asGrants(selected))}
        >
          {busy ? "Saving…" : "Save changes"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          tone="neutral"
          size="sm"
          disabled={busy}
          onClick={() => {
            if (dirty) setSelected(new Map(person.areas.map((g) => [g.area, g.access])));
            else onClose();
          }}
        >
          {dirty ? "Reset" : "Close"}
        </Button>
        <span className="ad-editor__summary" data-dirty={dirty || undefined}>
          {summary === ""
            ? "Tick an area to grant it, untick to revoke it, and pick how much of it."
            : summary}
        </span>
        <Button
          type="button"
          variant="outline"
          tone="danger"
          size="sm"
          disabled={busy || person.areas.length === 0}
          onClick={() => submit([])}
        >
          Remove all access
        </Button>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ""}
        message={pending?.message ?? ""}
        confirmLabel={pending?.confirmLabel}
        tone="danger"
        busy={busy}
        onConfirm={() => {
          if (pending) saveMutation.mutate(pending.next);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

const PERSON_SORT = {
  person: (p: GrantedPerson) => p.email,
  areas: (p: GrantedPerson) => p.areas.length,
  grantedBy: (p: GrantedPerson) => grantorLabels(p)[0] ?? null,
  granted: (p: GrantedPerson) => p.lastGrantedAt,
} as const;

/** Two repeating columns, and both are how this table is actually read: "who
 *  holds System Admin" and "everything that person handed out". Email is now
 *  unique per row and Granted is a timestamp, so those keep the search box and
 *  the sort caret instead.
 *
 *  Area is DERIVED rather than a closed list of `AREA_LIST` on purpose: a person
 *  can hold an area the backend added ahead of this bundle, and it must be
 *  filterable rather than silently missing. `order` puts the ones we do know in
 *  the nav's own order, so the popover reads like the sidebar. Both columns are
 *  multi-valued after grouping: someone with five areas granted by two admins
 *  matches every one of those seven selections, which is what an audit means by
 *  "show me who Dana let in". */
const PERSON_FILTERS: readonly FilterColumn<GrantedPerson>[] = [
  {
    key: "area",
    label: "Filter by area",
    allLabel: "All areas",
    value: (p) => p.areas.map((g) => g.area),
    optionLabel: (value) => AREA_META[value as Area]?.label ?? value,
    order: AREA_LIST.map((a) => a.key),
  },
  {
    // "Who can only look" is the question this whole level exists to answer, so
    // it is one click rather than a scan down the badge column. Multi-valued
    // like the area column: someone holding one area at each level matches both.
    key: "access",
    label: "Filter by access",
    allLabel: "Any access",
    value: (p) => p.areas.map((g) => g.access),
    optionLabel: (value) => ACCESS_META[value as Access]?.label ?? value,
    order: [...ACCESS_LEVELS],
  },
  {
    key: "grantedBy",
    label: "Filter by who granted it",
    allLabel: "Anyone",
    // `grantorLabels` already carries the seeded/migrated rows as their own
    // value, so a person with a mix of provenance stays selectable under both.
    value: (p) => grantorLabels(p),
  },
];

function PeopleTable({ people, canManage }: { people: GrantedPerson[]; canManage: boolean }) {
  const [filter, setFilter] = useRestoredState("permissions/people/filter", "");
  const [openEmail, setOpenEmail] = useState<string | null>(null);

  const columnFilters = useColumnFilters(people, PERSON_FILTERS, {
    restore: "permissions/people/filters",
  });
  const filtered = useMemo(
    () =>
      columnFilters.rows.filter((p) =>
        matchesQuery(
          filter,
          p.email,
          p.areas.map((g) => `${AREA_META[g.area].label} ${ACCESS_META[g.access].label}`).join(" "),
          grantorLabels(p).join(" "),
        ),
      ),
    [columnFilters.rows, filter],
  );
  const sorted = useTableSort(filtered, PERSON_SORT, null, {
    restore: "permissions/people/sort",
  });
  const paged = usePagedRows(sorted.rows, { restore: "permissions/people/window" });
  const narrowed = filter.trim() !== "" || columnFilters.active;

  if (people.length === 0) {
    return (
      <EmptyState
        title="Nobody has access yet"
        message={
          canManage ? "Grant an area using the form." : "Nobody holds an area in this environment."
        }
      />
    );
  }

  return (
    <>
      <div className="rc-toolbar">
        <Input
          type="search"
          inputSize="sm"
          placeholder="Filter by email, area, or who granted it…"
          aria-label="Filter people"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <TableFilters
          controls={columnFilters.controls}
          active={narrowed}
          onClear={() => {
            setFilter("");
            columnFilters.clear();
          }}
        />
        <span className="rc-toolbar__count">
          {filtered.length} of {people.length}
        </span>
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          title={filter ? `Nobody matches “${filter}”` : "Nobody matches those filters"}
        />
      ) : (
        <div className="rc-table-wrap">
          <table className="rc-table">
            <TableExport name="permissions" label="permissions" />
            <thead>
              <tr>
                <SortableTh sortKey="person" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Person
                </SortableTh>
                <SortableTh sortKey="areas" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Areas
                </SortableTh>
                <SortableTh sortKey="grantedBy" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Granted by
                </SortableTh>
                <SortableTh sortKey="granted" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Last change
                </SortableTh>
                {canManage ? <th className="rc-actions-col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {paged.shown.map((person) => {
                const open = openEmail === person.email;
                return (
                  <Fragment key={person.email}>
                    <tr className="ad-person-row" data-open={open || undefined}>
                      <td>
                        <span className="rc-cell-strong">{person.email}</span>
                      </td>
                      <td>
                        <div className="ad-area-cell">
                          {/* Tone carries the level, and the badge says it too: a
                              colour alone is not a permission anyone can read
                              out loud, and this column is what an access review
                              is looking at. */}
                          {person.areas.map(({ area, access }) => (
                            <Badge
                              key={area}
                              tone={access === "full" ? "info" : "neutral"}
                              size="sm"
                            >
                              {access === "full"
                                ? AREA_META[area].label
                                : `${AREA_META[area].label} (read only)`}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td>{grantorLabels(person).join(", ")}</td>
                      <td>{formatDateTime(person.lastGrantedAt)}</td>
                      {canManage ? (
                        <td className="rc-actions-col">
                          <div className="rc-row-actions">
                            <Button
                              type="button"
                              variant="outline"
                              tone="neutral"
                              size="sm"
                              aria-expanded={open}
                              onClick={() => setOpenEmail(open ? null : person.email)}
                            >
                              {open ? "Close" : "Manage"}
                            </Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                    {open ? (
                      <tr className="ad-editor-row">
                        <td colSpan={canManage ? 5 : 4}>
                          <AreaEditor
                            key={`${person.email}:${person.areas
                              .map((g) => `${g.area}=${g.access}`)
                              .join(",")}`}
                            person={person}
                            onClose={() => setOpenEmail(null)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </div>
      )}
    </>
  );
}

export function PermissionsPage() {
  const permissions = useQuery(permissionsQuery);
  const [platformControlOpen, setPlatformControlOpen] = useState(false);
  const people = useMemo(() => groupGrantsByPerson(permissions.data ?? []), [permissions.data]);
  // System Admin has both halves, and this page is where the difference shows:
  // the grant LIST is a `read` route (auditing who reaches what), while granting,
  // revoking and the Red Button are `full`. A read-only System Admin therefore
  // reads this whole table and is drawn none of the controls — each of which the
  // server would refuse anyway.
  // See alerts.tsx: the controls hide while the grant query is pending, but the
  // read-only sentence waits until we actually know.
  const { canWrite: canManage, known: accessKnown } = useWriteAccess("system_admin");

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Permissions</h1>
        <p className="ad-page__sub">
          {accessKnown && !canManage
            ? "Who can access each admin area, and whether they can act in it. Your own access to System Admin is read only, so nothing here can be changed."
            : "Who can access each admin area, and whether they can act in it."}
        </p>
      </header>

      {canManage ? <BulkAddForm /> : null}

      {/* Rendered for a read-only System Admin too, with no form: the status card
          is the read half of this area (`GET /platform/status` is
          `SystemAdminReadDep`) and this is its only surface, so hiding the panel
          would make a level we grant unreachable. */}
      <PlatformControl
        open={platformControlOpen}
        onOpenChange={setPlatformControlOpen}
        canAct={canManage}
        accessKnown={accessKnown}
      />

      {/* THE A/B SLIDERS ARE BACK, and the link below stays. #1231 removed this
          card on the reasoning that /experiments offers the same two-step
          control for all six kinds, so a second copy of the dials was
          duplication. The reasoning was sound and the timing was not: operators
          were still driving the two product experiments from here, and removing
          the control they use before they have moved to its replacement is a
          migration done TO them rather than with them.

          So both exist for now. They are two paths onto one value, not two
          values: this card posts the legacy per-experiment dial route, the board
          posts the newer per-kind allocation route, and both land in
          `experiment_weights` — so a move made in either place is visible in the
          other on its next read. That is the property that makes running both
          safe; if it ever stops being true, this card goes, not the board.

          RETIRE IT when the product experiments are being driven from
          /experiments in practice. This is deliberately a temporary duplicate,
          not a decision to keep two consoles. */}
      <ExperimentDials canAct={canManage} accessKnown={accessKnown} />

      <section className="ad-section">
        <h2 className="ad-section__title">Experiments</h2>
        <p className="ad-section__sub">
          Every experiment the registry derives (product cohorts, prompt versions, injections), its
          allocation, and the guardrail strip that says whether an arm is safe to keep running.
          Assignment is sticky per user or project, so raising an allocation only adds people and
          lowering it removes exactly the same ones.
        </p>
        <Link className="ad-link" to="/experiments">
          Open experiments
        </Link>
      </section>

      {/* The committed-budget tiers: the minimum project amount at which a
          client is handed to a human and offered the Calendly scheduler. Same
          read/full split as the dials. */}
      <BudgetThresholds canAct={canManage} accessKnown={accessKnown} />

      {/* The prompt sections moved into the experiment register, which now
          opens the same panel for all six kinds instead of one. Linking to the
          register filtered to sections lands a reader exactly where this card
          used to, with search and the other five kinds a filter away. */}
      <section className="ad-section">
        <h2 className="ad-section__title">Prompt sections</h2>
        <p className="ad-section__sub">
          What each agent's prompt is built from, and who is getting which version of every part. A
          dial sets who has a part at all; an allocation sets which version they get.
        </p>
        <Link className="ad-link" to="/experiments" search={{ kind: "section" }}>
          Open prompt sections
        </Link>
      </section>

      <section className="ad-section">
        <h2 className="ad-section__title">People with access</h2>
        {permissions.isPending ? (
          <TableSkeleton columns={canManage ? 5 : 4} />
        ) : permissions.isError ? (
          <AreaBoundary
            area="system_admin"
            error={permissions.error}
            onRetry={() => void permissions.refetch()}
          />
        ) : (
          <PeopleTable people={people} canManage={canManage} />
        )}
      </section>
    </div>
  );
}
